/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1678838400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Mar 15 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mixpanel",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Project ID",
          "depth": 1,
          "id": "project-id"
        }, {
          "value": "Project token",
          "depth": 1,
          "id": "project-token"
        }, {
          "value": "Service account",
          "depth": 1,
          "id": "service-account"
        }, {
          "value": "Group keys",
          "depth": 1,
          "id": "group-keys"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Objects",
          "depth": 1,
          "id": "objects"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Lookup tables",
          "depth": 1,
          "id": "lookup-tables"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3",
      img: "img",
      br: "br",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Ideally, your data warehouse is the single source of truth for all your customer data.\nWhen that's the case, using your warehouse as the source for your Mixpanel data enables richer product-usage analysis.\nYou can sync enriched data from your warehouse to create more granular segments on Mixpanel as well as bring in data from your engagement tools.\nMoreover, you can ensure that your product and growth teams work with data that's always up-to-date and in the expected format.\nLastly, if your company also uses a Business Intelligence tool such as Looker or Mode, you can ensure that the data is consistent across both systems."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Objects"
              })
            }), _jsx(_components.td, {
              children: "Sync records to objects such as users or organizations (groups) in your destination."
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to your destination. This is often in the form of a track call."
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Lookup tables"
              })
            }), _jsx(_components.td, {
              children: "Sync records as a CSV file to enrich event and profile properties within Mixpanel."
            }), _jsx(_components.td, {
              children: "All"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Upsert"
          }), " mode, new users and groups are inserted into Mixpanel and all attributes are kept up-to-date."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Insert"
          }), " mode, new events are inserted as track calls."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "All"
          }), " sync mode, the entire lookup table is replaced with each sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To configure your Mixpanel destination you need to provide the following information:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Project ID"
        }), "\n", _jsx(_components.li, {
          children: "Project Token"
        }), "\n", _jsx(_components.li, {
          children: "Service Account Username and Secret"
        }), "\n", _jsx(_components.li, {
          children: "Group Keys, when syncing group objects"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "project-id",
        children: "Project ID"
      }), "\n", _jsx(_components.p, {
        children: "To find your Project ID, click the Settings icon, then Project Settings.\nThen navigate to Overview > Project Details."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-project-id.png",
          alt: "Mixpanel Project ID"
        })
      }), "\n", _jsx(_components.h3, {
        id: "project-token",
        children: "Project token"
      }), "\n", _jsx(_components.p, {
        children: "To find your Project Token, click the Settings icon, then Project Settings.\nThen navigate to Overview > Access Keys."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-project-token.png",
          alt: "Mixpanel Project Token"
        })
      }), "\n", _jsx(_components.h3, {
        id: "service-account",
        children: "Service account"
      }), "\n", _jsxs(_components.p, {
        children: ["To receive your Service Account Username and Secret you must first create a Service Account.", _jsx(_components.br, {}), "\n", "Click the Settings icon, then Project Settings.\nThen navigate to Service Accounts > Add Service Account and copy the ", _jsx(_components.strong, {
          children: "Username"
        }), " and ", _jsx(_components.strong, {
          children: "Secret"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-service-account.png",
          alt: "Mixpanel Service Account"
        })
      }), "\n", _jsxs(Info, {
        children: [" Be sure to give your Service Account ", _jsx(_components.strong, {
          children: "Owner"
        }), " or ", _jsx(_components.strong, {
          children: "Admin"
        }), " permissions. "]
      }), "\n", _jsx(_components.h3, {
        id: "group-keys",
        children: "Group keys"
      }), "\n", _jsxs(_components.p, {
        children: ["If you are syncing group profiles or assigning users to groups, you need to indicate which Group Key to use.\nTo find your Group Keys, click the Settings icon, then Project Settings.\nThen navigate to Overview > Group Keys. Copy the appropriate ", _jsx(_components.strong, {
          children: "Key"
        }), " value."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-group-keys.png",
          alt: "Example Group Keys in the Mixpanel UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "objects",
        children: "Objects"
      }), "\n", _jsx(_components.p, {
        children: "You can decide to sync either User Objects or Group Objects."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-user-group.png",
          alt: "Selecting the User object in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing Group Objects, enter the ", _jsx(_components.a, {
          href: "#group-keys",
          children: "appropriate group key"
        }), " into the ", _jsx(_components.strong, {
          children: "Mixpanel group key"
        }), " field."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-org-id.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match records from your source to your Mixpanel workspace by Mixpanel User ID, email or Group ID."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-record-matching.png",
          alt: "Matching records on the Mixpanel UserID in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to your default and custom fields in Mixpanel.\nIf you're syncing User Objects, you can see two sections: one for native Mixpanel fields and one for custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-field-mapping-objects-1.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you're syncing Group Objects, you can only see one section."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-field-mapping-objects-2.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "users-location",
        children: "Users location"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, users uploaded using Hightouch won't have any associated location in Mixpanel.\nTo set a location, provide an IP address (field ", _jsx(_components.code, {
          children: "$ip"
        }), "), or longitude and latitude coordinates (fields ", _jsx(_components.code, {
          children: "$longitude"
        }), " and ", _jsx(_components.code, {
          children: "$latitude"
        }), ").\nFor more information, you can visit ", _jsx(_components.a, {
          href: "https://help.mixpanel.com/hc/en-us/articles/115004499343-Tracking-Geolocation-with-Server-Side-Implementation",
          children: "Mixpanel's official documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "assigning-users-to-groups",
        children: "Assigning users to groups"
      }), "\n", _jsxs(_components.p, {
        children: ["To assign users to groups, create a sync for User Objects.\nThen, create a column in your model which contains the group IDs that you want to assign the user to.\nFor example, to sync organizations, use a column called ", _jsx(_components.code, {
          children: "organizations"
        }), " containing either a group ID (", _jsx(_components.code, {
          children: "google"
        }), ") or an array of group IDs (", _jsx(_components.code, {
          children: "[google, apple, microsoft]"
        }), ").\nThen, ", _jsx(_components.a, {
          href: "#group-keys",
          children: "find your group key"
        }), ", and use it as the destination field to sync to."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-user-to-groups.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports sending events of a given name, for example, ", _jsx(_components.code, {
          children: "Signed Up"
        }), "."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.p, {
        children: "Hightouch lets you choose the column containing the event timestamp."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-track-event.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: " Hightouch accepts various date time formats for the timestamp and automatically converts them to the format required by Mixpanel. "
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match records from your source to your Mixpanel workspace by Mixpanel User ID."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-record-matching-events.png",
          alt: "Matching events records in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to pass data to the event properties of a Mixpanel event."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-field-mappings-events.png",
          alt: "Mapping fields in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "assigning-events-to-groups",
        children: "Assigning events to groups"
      }), "\n", _jsxs(_components.p, {
        children: ["To assign events to groups, create a sync to events.\nThen, create a column in your model which contains the group IDs that you want to assign the event to.\n", _jsx(_components.br, {}), "\n", "For example, to sync organizations, use a column called ", _jsx(_components.code, {
          children: "organizations"
        }), " containing either a group ID (", _jsx(_components.code, {
          children: "google"
        }), ") or an array of group IDs (", _jsx(_components.code, {
          children: "[google, apple, microsoft]"
        }), ").\nThen, ", _jsx(_components.a, {
          href: "#group-keys",
          children: "find your group key"
        }), ", and use it as the destination field to sync to."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-events-groups.png",
          alt: "Assigning events to a group using group key in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "lookup-tables",
        children: "Lookup tables"
      }), "\n", _jsxs(_components.p, {
        children: ["To sync data to your Lookup tables, choose ", _jsx(_components.strong, {
          children: "Enrichment"
        }), ".\nThen choose the specific Lookup table you want to replace."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-lookup-tables-sync-configuration.png",
          alt: "Syncing to Look up tables in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match records from your source to your Mixpanel workspace by the ID mapping of your choice.\nThe ID mapping should reference the unique identifier field of the events or profiles you are seeking to enrich."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-lookup-tables-record-matching.png",
          alt: "Matching records on the \"ID\" field in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You may also sync columns from your source to your Mixpanel Lookup table's custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-field-mappings-lookup-tables.png",
          alt: "Field mapping to custom fields in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "creating-lookup-tables-in-mixpanel",
        children: "Creating lookup tables in Mixpanel"
      }), "\n", _jsx(_components.p, {
        children: "To create Lookup tables, you can import them directly in Mixpanel.\nYou can do this by clicking on the Data Management icon and then on Lexicon.\nNavigate to Lookup Tables > Import > Lookup Table."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mixpanel-create-lookup-table.png",
          alt: "Creating lookup tables in the Mixpanel UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsx(_components.p, {
        children: "Mixpanel's Lookup table endpoint comes with the following limits:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Only 100 requests are allowed in a rolling 24 hour period. Please be mindful of this when choosing your sync schedule. Mixpanel recommends updating Lookup tables at most hourly."
        }), "\n", _jsx(_components.li, {
          children: "A Lookup table must not exceed 100 MB uncompressed (roughly 1-2M rows)."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "changing-projects",
        children: "Changing projects"
      }), "\n", _jsx(_components.p, {
        children: "If you need to change your Project ID, you also need to update the API Key that you originally provided.\nProject ID and API keys are paired together in the Mixpanel platform."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
